import { create } from 'zustand';
import { getClient } from '@matix/api-client';
import {
  readAuth,
  saveAuth,
  clearAuth,
  isSessionExpired,
  type AuthData,
  type AuthSubsidiary,
  type AuthBranch,
  type AuthDrawer,
  type AuthUser,
} from '../lib/cashierAuthClient';
import { offerBiometricAfterLogin } from '../features/auth/biometricOffer';
import { useUI } from './ui';
import { hasSignInLink } from '../lib/demoLink';
import {
  loginRequest,
  selectSubsidiaryRequest,
  selectBranchRequest,
  selectDrawerRequest,
  AuthApiError,
} from '../lib/cashierAuthApi';

/**
 * Cashier multi-tenant auth state machine (Phase 8.1).
 *
 *   unauthenticated → login → pending → choosing-subsidiary → subsidiary-chosen
 *     → choosing-branch → branch-chosen → choosing-drawer → session
 *
 * Selection rules:
 *   - subsidiary picker always shown (even with a single company)
 *   - branch picker always shown (even with a single branch)
 *   - cash drawer is auto-picked (first drawer in the branch) — no drawer step
 *   - 0 drawers → server returns 403 NO_DRAWERS, surface to user
 *
 * Once 'session' is reached, the existing cashier app (App.tsx, ShiftOpen,
 * etc.) takes over via isAuthenticated().
 */

export type AuthState =
  | { kind: 'unauthenticated'; banner: string | null }
  | { kind: 'logging-in' }
  | { kind: 'pending'; user: AuthUser; subsidiaries: AuthSubsidiary[]; pendingToken: string }
  | { kind: 'choosing-subsidiary'; user: AuthUser; subsidiaries: AuthSubsidiary[]; pendingToken: string }
  | { kind: 'subsidiary-chosen'; user: AuthUser; subsidiary: AuthSubsidiary; branches: AuthBranch[]; pendingToken: string }
  | { kind: 'choosing-branch'; user: AuthUser; subsidiary: AuthSubsidiary; branches: AuthBranch[]; pendingToken: string }
  | { kind: 'branch-chosen'; user: AuthUser; subsidiary: AuthSubsidiary; branch: AuthBranch; drawers: AuthDrawer[]; pendingToken: string }
  | { kind: 'choosing-drawer'; user: AuthUser; subsidiary: AuthSubsidiary; branch: AuthBranch; drawers: AuthDrawer[]; pendingToken: string }
  | { kind: 'session'; data: Extract<AuthData, { stage: 'session' }> }
  /**
   * The tenant has not finished its Initial Setup Wizard, so the server issued
   * no session. The credentials are held in memory only, to sign in again the
   * moment the wizard completes.
   */
  | { kind: 'setup'; slug: string; password: string; tenantName: string; setupToken: string | null };

interface AuthStore {
  state: AuthState;
  error: string | null;

  /** No username: the till signs in on company + password alone. */
  login: (slug: string, password: string, terminalCode?: string) => Promise<void>;
  chooseSubsidiary: (id: number) => Promise<void>;
  chooseBranch: (id: number) => Promise<void>;
  chooseDrawer: (id: number) => Promise<void>;
  /** The wizard finished — sign in again with the credentials it was opened with. */
  finishSetup: (owner?: { username: string; password: string }) => Promise<void>;
  /** Leave the wizard without finishing it. */
  exitSetup: () => void;
  backToSubsidiaryPicker: () => void;
  backToBranchPicker: () => void;
  logout: (banner?: string) => void;
  onAuthExpired: (banner?: string) => void;
  clearError: () => void;
}

/**
 * Session-storage key used to carry a login-screen banner across the hard
 * reload that logout / session-expiry performs (see `hardResetToLogin`). The
 * store is re-created from scratch after the reload, so an in-memory banner
 * would be lost — we stash it here and consume it in `initialState`.
 */
const LOGOUT_BANNER_KEY = 'pos:cashier:logout-banner';

/**
 * Log out by reloading the app back to the login screen.
 *
 * A logout / expiry must not leave any previous-tenant state behind: the cart
 * (`store/cart.ts`), the react-query catalog cache (not tenant-scoped, and the
 * full sync is cached with `staleTime: Infinity`), the open shift, parked
 * orders, customers, the tax rate, … are all root-level singletons that
 * survive a plain React state transition. If we only flipped `state` back to
 * `unauthenticated`, signing into a *different* tenant would show the previous
 * tenant's items and cart. A hard reload drops the entire JS heap (and the
 * QueryClient, which has no persister) so the next login always starts clean —
 * and it stays correct as new per-tenant stores are added.
 */
function hardResetToLogin(banner: string | null): void {
  try {
    if (banner) sessionStorage.setItem(LOGOUT_BANNER_KEY, banner);
    else sessionStorage.removeItem(LOGOUT_BANNER_KEY);
  } catch { /* sessionStorage unavailable — banner is best-effort */ }
  try {
    window.location.reload();
  } catch {
    /* non-browser / jsdom: the caller's `set(unauthenticated)` fallback stands */
  }
}

function initialState(): AuthState {
  // Consume any banner stashed just before a logout-triggered reload.
  let banner: string | null = null;
  try {
    banner = sessionStorage.getItem(LOGOUT_BANNER_KEY);
    if (banner) sessionStorage.removeItem(LOGOUT_BANNER_KEY);
  } catch { /* sessionStorage unavailable */ }

  // A sign-in link (?tenant=&pin=) replaces whatever session this browser holds,
  // so opening a second demo company lands in that company, not the last one.
  if (hasSignInLink()) {
    clearAuth();
    return { kind: 'unauthenticated', banner: null };
  }

  const stored = readAuth();
  if (!stored) return { kind: 'unauthenticated', banner };
  if (stored.stage === 'session') {
    // Never restore an expired session. Doing so left @matix/auth's
    // AuthProvider holding a dead token → LegacyApp rendered null → #root
    // stayed empty behind the loading splash until a manual cache clear.
    if (isSessionExpired()) {
      clearAuth();
      return {
        kind: 'unauthenticated',
        banner: banner ?? 'Your sign-in expired. Please log in again.',
      };
    }
    return { kind: 'session', data: stored };
  }
  // Wipe partial-flow state from previous session.
  clearAuth();
  return { kind: 'unauthenticated', banner };
}

export const useCashierAuthStore = create<AuthStore>((set, get) => ({
  state: initialState(),
  error: null,

  login: async (slug, password, terminalCode) => {
    set({ state: { kind: 'logging-in' }, error: null });
    try {
      const result = await loginRequest({
        tenant_slug: slug, password,
        ...(terminalCode ? { terminal_code: terminalCode } : {}),
      });
      // Auto-skip the company picker when there is exactly one company —
      // pass straight through to branch selection.
      if (result.available_subsidiaries.length === 1) {
        const only = result.available_subsidiaries[0]!;
        set({
          state: {
            kind: 'choosing-subsidiary',
            user: result.user,
            subsidiaries: result.available_subsidiaries,
            pendingToken: result.pending_token,
          },
        });
        await get().chooseSubsidiary(only.id);
        return;
      }
      set({
        state: {
          kind: 'pending',
          user: result.user,
          subsidiaries: result.available_subsidiaries,
          pendingToken: result.pending_token,
        },
      });
      saveAuth({
        stage: 'pending',
        token: result.pending_token,
        saved_at: Date.now(),
        user: result.user,
        available_subsidiaries: result.available_subsidiaries,
      });
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'SETUP_REQUIRED') {
        const b = err.body ?? {};
        set({
          state: {
            kind: 'setup',
            slug,
            password,
            tenantName: typeof b.tenant_name === 'string' ? b.tenant_name : slug,
            setupToken: typeof b.setup_token === 'string' ? b.setup_token : null,
          },
          error: null,
        });
        return;
      }
      set({
        state: { kind: 'unauthenticated', banner: null },
        error: messageForError(err),
      });
    }
  },

  finishSetup: async (owner) => {
    const s = get().state;
    if (s.kind !== 'setup') return;
    await get().login(s.slug, owner?.password ?? s.password);
  },

  exitSetup: () => set({ state: { kind: 'unauthenticated', banner: null }, error: null }),

  chooseSubsidiary: async (subsidiaryId) => {
    const s = get().state;
    if (s.kind !== 'pending' && s.kind !== 'choosing-subsidiary') return;
    set({
      state: {
        kind: 'choosing-subsidiary',
        user: s.user,
        subsidiaries: s.subsidiaries,
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    try {
      const result = await selectSubsidiaryRequest(s.pendingToken, subsidiaryId);
      // Auto-skip the branch picker when there is exactly one branch AND a
      // drawer can be resolved for it — its own, or the one on the user's
      // profile. With neither there is no session to form, so we still show
      // the picker in that case to surface its disabled state.
      const onlyBranch =
        result.available_branches.length === 1 ? result.available_branches[0]! : null;
      if (onlyBranch && onlyBranch.has_cash_drawer) {
        set({
          state: {
            kind: 'subsidiary-chosen',
            user: s.user,
            subsidiary: result.subsidiary,
            branches: result.available_branches,
            pendingToken: result.pending_token,
          },
        });
        await get().chooseBranch(onlyBranch.id);
        return;
      }
      set({
        state: {
          kind: 'subsidiary-chosen',
          user: s.user,
          subsidiary: result.subsidiary,
          branches: result.available_branches,
          pendingToken: result.pending_token,
        },
      });
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 401) {
        get().onAuthExpired();
        return;
      }
      set({
        state: {
          kind: 'pending',
          user: s.user,
          subsidiaries: s.subsidiaries,
          pendingToken: s.pendingToken,
        },
        error: messageForError(err),
      });
    }
  },

  chooseBranch: async (branchId) => {
    const s = get().state;
    if (s.kind !== 'subsidiary-chosen' && s.kind !== 'choosing-branch') return;
    set({
      state: {
        kind: 'choosing-branch',
        user: s.user,
        subsidiary: s.subsidiary,
        branches: s.branches,
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    try {
      const result = await selectBranchRequest(s.pendingToken, branchId);
      // No drawer step: auto-pick the first drawer in the branch and go
      // straight to the session. The server guarantees at least one drawer
      // here (it returns 403 NO_DRAWERS otherwise), but guard defensively.
      const firstDrawer = result.available_cash_drawers[0];
      if (!firstDrawer) {
        set({
          state: {
            kind: 'subsidiary-chosen',
            user: s.user,
            subsidiary: s.subsidiary,
            branches: s.branches,
            pendingToken: s.pendingToken,
          },
          error: mapErrorCode('NO_DRAWERS'),
        });
        return;
      }
      set({
        state: {
          kind: 'branch-chosen',
          user: s.user,
          subsidiary: s.subsidiary,
          branch: result.branch,
          drawers: result.available_cash_drawers,
          pendingToken: result.pending_token,
        },
      });
      await get().chooseDrawer(firstDrawer.id);
      return;
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 401) {
        get().onAuthExpired();
        return;
      }
      set({
        state: {
          kind: 'subsidiary-chosen',
          user: s.user,
          subsidiary: s.subsidiary,
          branches: s.branches,
          pendingToken: s.pendingToken,
        },
        error: messageForError(err),
      });
    }
  },

  chooseDrawer: async (drawerId) => {
    const s = get().state;
    if (s.kind !== 'branch-chosen' && s.kind !== 'choosing-drawer') return;
    set({
      state: {
        kind: 'choosing-drawer',
        user: s.user,
        subsidiary: s.subsidiary,
        branch: s.branch,
        drawers: s.drawers,
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    try {
      const result = await selectDrawerRequest(s.pendingToken, drawerId);
      const drawer = s.drawers.find((d) => d.id === result.session.cash_drawer_id) ?? {
        id: result.session.cash_drawer_id,
        code: '',
        name: result.session.cash_drawer_name,
        branch_id: result.session.branch_id,
      };
      const data: Extract<AuthData, { stage: 'session' }> = {
        stage: 'session',
        token: result.token,
        saved_at: Date.now(),
        user: s.user,
        subsidiary: s.subsidiary,
        branch: s.branch,
        drawer,
        session: result.session,
      };
      saveAuth(data);
      // Phone shell only (a no-op in a browser): remember the workspace and,
      // the first time, offer to unlock with the fingerprint / face next time.
      void offerBiometricAfterLogin(data);
      // Make the app authenticated: the session JWT is a drop-in for the
      // legacy login token, so feeding it to the shared api-client means
      // every API call carries it AND @matix/auth's AuthProvider (mounted
      // around LegacyApp) reads it on mount. Land directly on the main POS
      // shell — opening a shift is optional, not a mandatory gate.
      getClient().setToken(result.token);
      useUI.getState().setScreen('app');
      set({ state: { kind: 'session', data } });
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 401) {
        get().onAuthExpired();
        return;
      }
      set({
        state: {
          kind: 'branch-chosen',
          user: s.user,
          subsidiary: s.subsidiary,
          branch: s.branch,
          drawers: s.drawers,
          pendingToken: s.pendingToken,
        },
        error: messageForError(err),
      });
    }
  },

  backToSubsidiaryPicker: () => {
    const s = get().state;
    if (s.kind !== 'subsidiary-chosen' && s.kind !== 'branch-chosen') return;
    set({
      state: {
        kind: 'pending',
        user: s.user,
        subsidiaries: [],
        pendingToken: s.pendingToken,
      },
      error: null,
    });
  },

  backToBranchPicker: () => {
    const s = get().state;
    if (s.kind !== 'branch-chosen') return;
    set({
      state: {
        kind: 'subsidiary-chosen',
        user: s.user,
        subsidiary: s.subsidiary,
        branches: [],
        pendingToken: s.pendingToken,
      },
      error: null,
    });
  },

  logout: (banner) => {
    clearAuth();
    getClient().clearToken();
    // Fallback for non-browser/test envs where reload is a no-op; in the
    // browser the reload below replaces this state anyway.
    set({ state: { kind: 'unauthenticated', banner: banner ?? null }, error: null });
    // Hard reload to drop all previous-tenant in-memory state + query cache
    // (cart, catalog, shift, …) so the next login can't inherit stale data.
    hardResetToLogin(banner ?? null);
  },

  onAuthExpired: (banner) => {
    clearAuth();
    getClient().clearToken();
    const msg = banner ?? 'Your sign-in expired. Please log in again.';
    set({ state: { kind: 'unauthenticated', banner: msg }, error: null });
    hardResetToLogin(msg);
  },

  clearError: () => set({ error: null }),
}));

function messageForError(err: unknown): string {
  if (err instanceof AuthApiError) return mapErrorCode(err.code);
  if (err instanceof Error) return `Network error: ${err.message}`;
  return 'Unexpected error. Please try again.';
}

function mapErrorCode(code: string): string {
  switch (code) {
    case 'INVALID_CREDENTIALS': return 'Wrong password for this company.';
    // Two users share this password, so it no longer names one person. We
    // refuse rather than guess — the wrong cashier would own the shift.
    case 'AMBIGUOUS_PASSWORD':
      return 'More than one user has this password. Ask your manager for a unique one.';
    case 'ACCOUNT_INACTIVE': return 'Account is not activated.';
    case 'ACCOUNT_LOCKED': return 'Account temporarily locked. Try again later.';
    case 'ROLE_NOT_ALLOWED': return 'This account is not authorized to use the cashier app.';
    case 'NO_COMPANY_ACCESS': return 'This account has no company access.';
    case 'TENANT_UNAVAILABLE': return 'Server unavailable. Try again in a moment.';
    case 'INVALID_TENANT_SLUG': return 'Unknown tenant. Check the tenant name.';
    case 'FORBIDDEN_SUBSIDIARY': return 'You no longer have access to this company.';
    case 'FORBIDDEN_BRANCH': return 'You no longer have access to this branch.';
    case 'WRONG_SUBSIDIARY': return 'This branch is not part of the chosen company.';
    case 'NO_DRAWERS': return 'This branch has no cash drawer configured. Contact your administrator.';
    case 'FORBIDDEN_DRAWER': return 'Drawer not available in this branch.';
    // mstr_CashDrawer.IsActive = 0 — switched off by the admin, not missing.
    case 'DRAWER_INACTIVE': return 'This cash drawer is inactive. Contact your administrator.';
    case 'WRONG_BRANCH': return 'Drawer does not belong to the chosen branch.';
    case 'INVALID_TOKEN': return 'Your sign-in expired. Please log in again.';
    case 'INVALID_REQUEST': return 'Some required fields are missing.';
    default: return `Sign-in failed (${code}).`;
  }
}
